import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { formatCurrency, formatNumber } from '@/lib/format';
import { toast } from 'sonner';
import { Building2, Calendar, DollarSign, FileText, TrendingUp, Users } from 'lucide-react';

interface ListingDetailModalProps {
  listing: any;
  open: boolean;
  onClose: () => void;
}

export function ListingDetailModal({ listing, open, onClose }: ListingDetailModalProps) {
  const [offerAmount, setOfferAmount] = useState('');
  const [offerNotes, setOfferNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmitOffer = async () => {
    if (!offerAmount || parseFloat(offerAmount) <= 0) {
      toast.error('Please enter a valid offer amount');
      return;
    }

    setSubmitting(true);
    try {
      // TODO: Call edge function to submit offer
      toast.success('Offer submitted successfully');
      onClose();
      setOfferAmount('');
      setOfferNotes('');
    } catch (error) {
      toast.error('Failed to submit offer');
    } finally {
      setSubmitting(false);
    }
  };

  if (!listing) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-2xl">{listing.portfolio_name}</DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-1">
                <Building2 className="h-4 w-4" />
                {listing.seller_org}
              </DialogDescription>
            </div>
            <Badge variant={listing.status === 'active' ? 'default' : 'secondary'}>
              {listing.status}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Asking Price</div>
              <div className="text-2xl font-bold text-primary">
                {formatCurrency(listing.asking_price)}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-sm text-muted-foreground">Nominal Value</div>
              <div className="text-xl font-semibold">
                {formatCurrency(listing.nominal_value)}
              </div>
            </div>
          </div>

          <Separator />

          {/* Portfolio Details */}
          <div className="space-y-3">
            <h3 className="font-semibold">Portfolio Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span>{formatNumber(listing.account_count)} accounts</span>
              </div>
              {listing.irr_target && (
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  <span>{listing.irr_target}% IRR Target</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span>
                  {((listing.asking_price / listing.nominal_value) * 100).toFixed(1)}% of face
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span>Listed {new Date(listing.listed_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {listing.description && (
            <>
              <Separator />
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Description
                </h3>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {listing.description}
                </p>
              </div>
            </>
          )}

          {listing.status === 'active' && (
            <>
              <Separator />
              {/* Make Offer Section */}
              <div className="space-y-4">
                <h3 className="font-semibold">Make an Offer</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="offer">Offer Amount *</Label>
                    <Input
                      id="offer"
                      type="number"
                      placeholder="Enter your offer"
                      value={offerAmount}
                      onChange={(e) => setOfferAmount(e.target.value)}
                      className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Any additional information for the seller..."
                      value={offerNotes}
                      onChange={(e) => setOfferNotes(e.target.value)}
                      rows={3}
                    />
                  </div>
                  <Button
                    onClick={handleSubmitOffer}
                    disabled={submitting}
                    className="w-full"
                  >
                    {submitting ? 'Submitting...' : 'Submit Offer'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
